import { fetchWithAuth, BASE_URL } from "../api/client";

/**
 * Team Chat Service - Real API Integration
 * Uses the existing fetchWithAuth utility for authenticated requests
 */

export const ChatService = {
    /**
     * List all conversations for the current user
     * GET /api/chat/conversations
     * @param {Object} options - Optional settings
     * @param {boolean} options.skipGlobalAuthHandler - If true, don't trigger global logout on auth failure
     */
    getConversations: async (options = {}) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations`, {
            headers: { accept: "application/json" },
            skipGlobalAuthHandler: options.skipGlobalAuthHandler || false
        });

        if (!response.ok) {
            throw new Error("Failed to fetch conversations");
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Create or get a conversation with specified participants
     * POST /api/chat/conversations
     * Body: { participant_emails: [...], name?: "Group Name" }
     *
     * API UPDATE NEEDED:
     * - Backend should accept optional 'name' field for group chats
     * - Response should include conversation.name if provided
     */
    startConversation: async (payload) => {
        // Support both old (array) and new (object with participants/name) formats
        const requestBody = Array.isArray(payload)
            ? { participant_emails: payload }
            : {
                participant_emails: payload.participants || payload.participant_emails || [],
                ...(payload.name && { name: payload.name })
            };


        const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error("❌ Failed to create conversation:", response.status, errorText);
            let errorMessage = "Failed to create conversation";
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.detail || errorJson.message || errorMessage;
            } catch (e) {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Get messages for a specific conversation
     * GET /api/chat/conversations/{conversation_id}/messages
     */
    getMessages: async (conversationId, options = {}) => {
        const { limit = 50, before } = options;

        let url = `${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}/messages?limit=${limit}`;
        if (before) {
            url += `&before=${encodeURIComponent(before)}`;
        }

        const response = await fetchWithAuth(url, {
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            throw new Error("Failed to fetch messages");
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Send a message to a conversation
     * POST /api/chat/messages
     *
     * @param {string} conversationId - The conversation ID
     * @param {string} text - Message text
     * @param {Object[]} attachments - Array of attachment objects with id, filename, file_type, s3_key
     * @param {Object[]} linkPreviews - Array of link preview objects
     * @param {string|null} replyToId - Optional message ID being replied to
     */
    sendMessage: async (conversationId, text, attachments = [], linkPreviews = [], replyToId = null) => {
        try {
            // Format attachments for backend - requires id, filename, file_type, s3_key
            const formattedAttachments = attachments.map(att => ({
                id: att.id,
                filename: att.filename || att.name || 'attachment',
                file_type: att.file_type || att.type || att.contentType || 'application/octet-stream',
                s3_key: att.s3_key || att.objectKey || att.object_key
            }));

            const payload = {
                conversation_id: conversationId,
                text,
                attachments: formattedAttachments,
                link_previews: linkPreviews
            };

            // Add reply_to_id if replying to a message
            if (replyToId) {
                payload.reply_to_id = replyToId;
            }

            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || "Failed to send message");
            }

            return await response.json();
        } catch (error) {
            console.error("SendMessage Error:", error);
            throw error;
        }
    },

    /**
     * Toggle emoji reaction on a message
     * POST /api/chat/messages/{messageId}/reactions
     * Body: { emoji: "👍" }
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Should toggle (add if not reacted, remove if already reacted)
     * - Response should include updated reaction counts
     * - Format: { emoji: "👍", count: 5, userReacted: true }
     */
    toggleReaction: async (messageId, emoji) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}/reactions`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            },
            body: JSON.stringify({ emoji })
        });

        if (!response.ok) {
            throw new Error("Failed to toggle reaction");
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Get all reactions for a message
     * GET /api/chat/messages/{messageId}/reactions
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Response format: [{ emoji: "👍", count: 3, userReacted: true }, ...]
     */
    getReactions: async (messageId) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}/reactions`, {
            headers: { accept: "application/json" }
        });

        if (!response.ok) {
            throw new Error("Failed to fetch reactions");
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Upload a file attachment for chat messages
     * POST /api/chat/attachments/upload
     */
    uploadAttachment: async (file) => {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const response = await fetchWithAuth(`${BASE_URL}/api/chat/attachments/upload`, {
            method: "POST",
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error("❌ Failed to upload attachment:", response.status, errorText);
            throw new Error("Failed to upload attachment");
        }

        const data = await response.json();
        const payload = data.data || data;

        return {
            status: response.status,
            data: {
                ...payload,
                filename: payload?.filename || payload?.name || file.name,
                file_type: payload?.file_type || payload?.type || file.type,
                file_size: payload?.file_size || file.size,
                objectKey: payload?.objectKey || payload?.object_key || payload?.s3_key
            }
        };
    },

    /**
     * Get a signed URL for a chat attachment
     * GET /api/chat/attachments/signed-url?objectKey={key}
     */
    getAttachmentSignedUrl: async (objectKey) => {
        const key = encodeURIComponent(objectKey || "");
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/attachments/signed-url?objectKey=${key}`);

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error("❌ Failed to get attachment signed URL:", response.status, errorText);
            throw new Error("Unable to load attachment preview.");
        }

        const data = await response.json();
        return {
            status: response.status,
            data: data.data || data,
            url: data.data?.url || data.data?.signedUrl || data.url || data.signedUrl
        };
    },

    /**
     * Generate a rich link preview for a URL
     * POST /api/chat/link-preview
     *
     * MOCK IMPLEMENTATION FOR DEMO
     */
    generateLinkPreview: async (url) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/link-preview`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify({ url })
            });

            if (response.ok) {
                const data = await response.json();
                return { status: response.status, data: data.data || data };
            }
        } catch (error) {
            console.warn("Real link preview failed, using mock:", error);
        }

        // FALLBACK MOCK IMPLEMENTATION FOR DEMO
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));

        // Basic mock data (keep it neutral to avoid demo copy in UI)
        let hostname = "Website";
        try {
            hostname = new URL(url).hostname;
        } catch {
            hostname = "Website";
        }
        let mockData = {
            url: url,
            title: hostname,
            description: "",
            image: null,
            site_name: hostname,
            type: "website"
        };

        // Special handling for YouTube mock
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            const videoId = url.match(/(?:v=|be\/)([\w-]{11})/) ? url.match(/(?:v=|be\/)([\w-]{11})/)[1] : 'dQw4w9WgXcQ';
            mockData = {
                ...mockData,
                title: "YouTube Video Preview (Mock)",
                site_name: "YouTube",
                type: "video.other",
                video_type: "youtube",
                image: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
                video_id: videoId,
                embed_url: `https://www.youtube.com/embed/${videoId}`
            };
        }

        return { status: 200, data: mockData };
    },

    /**
     * Mark a conversation as read
     * POST /api/chat/conversations/:id/read
     */
    markAsRead: async (conversationId) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${conversationId}/read`, {
                method: "POST",
                headers: { "Content-Type": "application/json" }
            });
            if (!response.ok) throw new Error("Failed to mark as read");
            return await response.json();
        } catch (error) {
            console.warn("MarkAsRead failed:", error);
            // Mock success for now, in case endpoint doesn't exist yet
            return { status: "success" };
        }
    },

    /**
     * Delete a conversation
     * DELETE /api/chat/conversations/{conversation_id}
     * Permanently deletes all messages and removes conversation from all participants
     */
    deleteConversation: async (conversationId) => {

        const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            }
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Unknown error");
            console.error("Delete conversation failed:", response.status, errorText);

            if (response.status === 404) {
                throw new Error("Conversation not found or you are not a participant.");
            }
            if (response.status === 403) {
                throw new Error("You don't have permission to delete this conversation.");
            }

            throw new Error("Failed to delete conversation. Please try again.");
        }

        // Handle empty response (204 No Content)
        const text = await response.text();
        if (!text) {
            return { status: response.status, data: { success: true } };
        }

        try {
            const data = JSON.parse(text);
            return { status: response.status, data: data.data || data };
        } catch {
            return { status: response.status, data: { success: true } };
        }
    },

    // ============================================
    // NEW ROCKET.CHAT-INSPIRED API METHODS
    // ============================================

    /**
     * Edit a message
     * PUT /api/chat/messages/{messageId}
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Should only allow editing own messages
     * - Should track edit history (edited: true, edited_at: timestamp)
     * - May have time limit for editing (e.g., within 15 minutes)
     */
    editMessage: async (messageId, newText) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify({ text: newText })
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "Failed to edit message");
                throw new Error(errorText);
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Edit message API not implemented, using optimistic update:", error);
            // Return mock success for frontend-only demo
            return { status: 200, data: { id: messageId, text: newText, edited: true } };
        }
    },

    /**
     * Delete a single message
     * DELETE /api/chat/messages/{messageId}
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Should only allow deleting own messages (or admin)
     * - May have time limit for deletion
     */
    deleteMessage: async (messageId) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}`, {
                method: "DELETE"
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "Failed to delete message");
                throw new Error(errorText);
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Delete message API not implemented:", error);
            // Return mock success for frontend-only demo
            return { status: 200, data: { deleted: true } };
        }
    },

    /**
     * Pin/Unpin a message
     * POST /api/chat/messages/{messageId}/pin
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Should toggle pin status
     * - Pinned messages should be retrievable separately
     * - May have limit on pinned messages per conversation
     */
    pinMessage: async (messageId, pin = true) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}/pin`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify({ pinned: pin })
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "Failed to pin message");
                throw new Error(errorText);
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Pin message API not implemented, using local state:", error);
            // Return mock success for frontend-only demo
            return { status: 200, data: { pinned: pin } };
        }
    },

    /**
     * Star/Unstar a message (personal bookmark)
     * POST /api/chat/messages/{messageId}/star
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Starred messages are personal (per-user)
     * - Should be retrievable in a "starred messages" list
     */
    starMessage: async (messageId, star = true) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}/star`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify({ starred: star })
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "Failed to star message");
                throw new Error(errorText);
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Star message API not implemented, using local state:", error);
            // Return mock success for frontend-only demo
            return { status: 200, data: { starred: star } };
        }
    },

    /**
     * Forward a message to another conversation
     * POST /api/chat/messages/{messageId}/forward
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Should copy message content to target conversation
     * - Should include "Forwarded from" metadata
     */
    forwardMessage: async (messageId, targetConversationId) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/${encodeURIComponent(messageId)}/forward`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    accept: "application/json"
                },
                body: JSON.stringify({ target_conversation_id: targetConversationId })
            });

            if (!response.ok) {
                const errorText = await response.text().catch(() => "Failed to forward message");
                throw new Error(errorText);
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Forward message API not implemented:", error);
            throw error;
        }
    },

    /**
     * Get pinned messages for a conversation
     * GET /api/chat/conversations/{conversationId}/pinned
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Returns list of pinned messages for the conversation
     */
    getPinnedMessages: async (conversationId) => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}/pinned`, {
                headers: { accept: "application/json" }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch pinned messages");
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Get pinned messages API not implemented:", error);
            return { status: 200, data: [] };
        }
    },

    /**
     * Get starred messages for current user
     * GET /api/chat/messages/starred
     *
     * API UPDATE NEEDED:
     * - Backend should implement this endpoint
     * - Returns list of user's starred messages across all conversations
     */
    getStarredMessages: async () => {
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/chat/messages/starred`, {
                headers: { accept: "application/json" }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch starred messages");
            }

            const data = await response.json();
            return { status: response.status, data: data.data || data };
        } catch (error) {
            console.warn("Get starred messages API not implemented:", error);
            return { status: 200, data: [] };
        }
    },

    // ============================================
    // GROUP CHAT PARTICIPANT MANAGEMENT
    // ============================================

    /**
     * Add a member to a group chat
     * POST /api/chat/conversations/{conversation_id}/participants
     * Body: { email: "newmember@example.com" }
     *
     * Validation Rules (Backend):
     * - Only works on group conversations (not 1:1 direct messages)
     * - Requester must be a current participant of the conversation
     */
    addParticipant: async (conversationId, email) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}/participants`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json"
            },
            body: JSON.stringify({ email })
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Failed to add participant");
            let errorMessage = "Failed to add participant";
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.detail || errorJson.message || errorMessage;
            } catch (e) {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return { status: response.status, data: data.data || data };
    },

    /**
     * Remove a member from a group chat
     * DELETE /api/chat/conversations/{conversation_id}/participants/{participant_email}
     *
     * Validation Rules (Backend):
     * - Only works on group conversations (not 1:1 direct messages)
     * - Requester must be a current participant of the conversation
     * - Cannot remove a member if it would leave fewer than 2 participants
     * - Users can remove themselves (leave the conversation)
     */
    removeParticipant: async (conversationId, participantEmail) => {
        const response = await fetchWithAuth(`${BASE_URL}/api/chat/conversations/${encodeURIComponent(conversationId)}/participants/${encodeURIComponent(participantEmail)}`, {
            method: "DELETE",
            headers: {
                accept: "application/json"
            }
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => "Failed to remove participant");
            let errorMessage = "Failed to remove participant";
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.detail || errorJson.message || errorMessage;
            } catch (e) {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        // Handle empty response (204 No Content)
        const text = await response.text();
        if (!text) {
            return { status: response.status, data: { removed: true } };
        }

        try {
            const data = JSON.parse(text);
            return { status: response.status, data: data.data || data };
        } catch {
            return { status: response.status, data: { removed: true } };
        }
    }
};
